import { useCallback, useMemo } from 'react';

/**
 * useHaptics - 2025 Haptic Feedback Hook
 *
 * Provides tactile feedback for mobile interactions following
 * iOS and Android haptic patterns.
 *
 * Haptic Types:
 * - light: UI feedback (button taps, toggles)
 * - medium: State changes (mode switches, selections)
 * - heavy: Important actions (session start/end)
 * - success: Positive outcomes (friend joined, achievement)
 * - warning: Attention needed (connection issues)
 * - error: Problems (failed actions)
 */
const useHaptics = () => {
  // Check if vibration API is available
  const isSupported = useMemo(() => {
    return typeof navigator !== 'undefined' && 'vibrate' in navigator;
  }, []);

  /**
   * Light haptic - Quick tap feedback
   * Use for: Button taps, toggles, list selections
   */
  const light = useCallback(() => {
    if (!isSupported) return;
    navigator.vibrate(10);
  }, [isSupported]);

  /**
   * Medium haptic - Noticeable feedback
   * Use for: Mode switches, important selections, slider snaps
   */
  const medium = useCallback(() => {
    if (!isSupported) return;
    navigator.vibrate(25);
  }, [isSupported]);

  /**
   * Heavy haptic - Strong feedback
   * Use for: Session start/end, major actions, confirmations
   */
  const heavy = useCallback(() => {
    if (!isSupported) return;
    navigator.vibrate(50);
  }, [isSupported]);

  /**
   * Success haptic - Double pulse pattern
   * Use for: Friend joined, achievement unlocked, task completed
   */
  const success = useCallback(() => {
    if (!isSupported) return;
    // Two quick pulses - feels celebratory
    navigator.vibrate([15, 50, 15]);
  }, [isSupported]);

  /**
   * Warning haptic - Attention-getting pattern
   * Use for: Connection issues, low battery, time warnings
   */
  const warning = useCallback(() => {
    if (!isSupported) return;
    // Three short pulses
    navigator.vibrate([20, 40, 20, 40, 20]);
  }, [isSupported]);

  /**
   * Error haptic - Strong alert pattern
   * Use for: Failed actions, errors, disconnections
   */
  const error = useCallback(() => {
    if (!isSupported) return;
    // Long-short-long pattern (feels urgent)
    navigator.vibrate([50, 30, 20, 30, 50]);
  }, [isSupported]);

  /**
   * Selection haptic - Very light tick
   * Use for: Scrolling through options, picker wheels
   */
  const selection = useCallback(() => {
    if (!isSupported) return;
    navigator.vibrate(5);
  }, [isSupported]);

  /**
   * Impact haptic - Collision/snap feel
   * Use for: Drag-drop snap, collision detection, PiP docking
   */
  const impact = useCallback((intensity = 'medium') => {
    if (!isSupported) return;
    const patterns = {
      light: 8,
      medium: 20,
      heavy: 40,
    };
    navigator.vibrate(patterns[intensity] || patterns.medium);
  }, [isSupported]);

  /**
   * Custom pattern - For special interactions
   * @param pattern - Array of vibrate/pause durations in ms
   */
  const custom = useCallback((pattern) => {
    if (!isSupported || !Array.isArray(pattern)) return;
    navigator.vibrate(pattern);
  }, [isSupported]);

  /**
   * Session start haptic - Distinctive "starting" feel
   * Use for: Starting study session, entering focus mode
   */
  const sessionStart = useCallback(() => {
    if (!isSupported) return;
    // Rising pattern - feels like powering up
    navigator.vibrate([10, 30, 20, 30, 30, 30, 50]);
  }, [isSupported]);

  /**
   * Session end haptic - Clean closing feel
   * Use for: Ending session, logging out
   */
  const sessionEnd = useCallback(() => {
    if (!isSupported) return;
    // Descending pattern - feels like winding down
    navigator.vibrate([40, 50, 20, 50, 10]);
  }, [isSupported]);

  /**
   * Friend joined haptic - Social notification
   * Use for: Friend entered room, new participant
   */
  const friendJoined = useCallback(() => {
    if (!isSupported) return;
    // Cheerful double-tap pattern
    navigator.vibrate([15, 80, 25]);
  }, [isSupported]);

  /**
   * Message received haptic - Subtle notification
   * Use for: New message, chat update
   */
  const messageReceived = useCallback(() => {
    if (!isSupported) return;
    navigator.vibrate([10, 40, 10]);
  }, [isSupported]);

  /**
   * Stop all haptics
   */
  const stop = useCallback(() => {
    if (!isSupported) return;
    navigator.vibrate(0);
  }, [isSupported]);

  return {
    // Core haptics
    light,
    medium,
    heavy,

    // State haptics
    success,
    warning,
    error,

    // UI haptics
    selection,
    impact,

    // App-specific haptics
    sessionStart,
    sessionEnd,
    friendJoined,
    messageReceived,

    // Utilities
    custom,
    stop,
    isSupported,
  };
};

export default useHaptics;
